package org.example.project.enginekalender

import org.example.project.service.ZustandManager
import org.example.project.utils.All

data class KalenderDayState(
    val bookings: List<All>,
    val count: Int,
    val colorHex: String?
)

fun kalenderDayState(bookings: List<All>): KalenderDayState {
    val visibleBookings = bookings.filter { kalenderBookingState(it) != KalenderBookingState.HIDDEN }
    val customColor = visibleBookings.firstNotNullOfOrNull { it.farbe?.takeIf(String::isNotBlank) }
    val colorHex = when {
        customColor != null -> customColor
        else -> visibleBookings.firstNotNullOfOrNull(::kalenderBookingColorHex)
    }
    return KalenderDayState(
        bookings = visibleBookings,
        count = visibleBookings.size,
        colorHex = colorHex
    )
}

fun kalenderVisibleBookings(bookings: List<All>): List<All> =
    kalenderDayState(bookings).bookings

fun kalenderBookingMarkerColorHex(bookings: List<All>): String? =
    kalenderDayState(bookings).colorHex

private fun kalenderBookingState(booking: All): KalenderBookingState {
    if (kalenderBookingIsVacation(booking)) {
        return when (ZustandManager.Urlaub.fromCode(booking.zustand)) {
            ZustandManager.Urlaub.BEANTRAGT,
            ZustandManager.Urlaub.IN_BEARBEITUNG -> KalenderBookingState.RESERVED
            ZustandManager.Urlaub.GENEHMIGT -> KalenderBookingState.OCCUPIED
            ZustandManager.Urlaub.VERWEIGERT,
            ZustandManager.Urlaub.STORNIERT -> KalenderBookingState.HIDDEN
            else -> KalenderBookingState.HIDDEN
        }
    }

    return when (booking.zustand) {
        ZustandManager.Termin.STORNIERT,
        ZustandManager.Slot.FREI -> KalenderBookingState.HIDDEN
        ZustandManager.Termin.ANGEMELDET,
        ZustandManager.RESERVIERT,
        ZustandManager.Slot.IN_BUCHUNG,
        ZustandManager.Slot.RESERVIERT -> KalenderBookingState.RESERVED
        else -> if (booking.belegt) KalenderBookingState.OCCUPIED else KalenderBookingState.RESERVED
    }
}

private fun kalenderBookingColorHex(booking: All): String? {
    if (kalenderBookingIsVacation(booking)) {
        kalenderAbsenceTypeColorHex(booking)?.let { return it }
        val code = ZustandManager.Urlaub.fromCode(booking.zustand)
        val genommen = code == ZustandManager.Urlaub.GENEHMIGT &&
            booking.datum.takeIf { parseKalenderIsoDate(it) != null }?.let { it < kalenderTodayIsoDate() } == true
        return ZustandManager.Urlaub.calendarColorHexFor(code, genommen)
    }

    return when {
        booking.art.equals("Termin", ignoreCase = true) -> ZustandManager.Termin.colorHexFor(booking.zustand)
        booking.art.equals("Slot", ignoreCase = true) -> ZustandManager.Slot.colorHexFor(booking.zustand)
        else -> ZustandManager.colorHexFor(booking.zustand)
    }
}

private fun kalenderAbsenceTypeColorHex(booking: All): String? =
    when (booking.abwesenheitsart.ifBlank { booking.bezeichnung }.trim().lowercase()) {
        "freizeitausgleich" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FREIZEITAUSGLEICH)
        "unbezahlt frei" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG)
        "krank", "krankheit", "krankmeldung", "krankenschein" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.KRANK)
        "sonderurlaub" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.URLAUB)
        else -> null
    }

fun kalenderBookingIsVacation(booking: All): Boolean {
    if (booking.urlaubstag == false) return false
    val labels = listOf(booking.art, booking.abwesenheitsart, booking.bezeichnung)
        .map { it.trim().lowercase() }
        .filter { it.isNotBlank() }
    val absenceLabels = setOf(
        "urlaub",
        "freizeitausgleich",
        "unbezahlt frei",
        "sonderurlaub",
        "krankheit",
        "homeoffice",
        "fehltag"
    )
    return booking.urlaub || booking.urlaubId.isNotBlank() || labels.any { it in absenceLabels }
}

fun kalenderBookingStatusText(booking: All): String =
    if (kalenderBookingIsVacation(booking)) {
        ZustandManager.Urlaub.textFor(booking.zustand)
    } else {
        ZustandManager.Termin.textFor(booking.zustand)
    }

private enum class KalenderBookingState {
    HIDDEN,
    RESERVED,
    OCCUPIED
}
